import signalRService from './signalr-service';
import firebaseService from './firebase-service';
import ShowNotification from '../components/react-notifications/react-notifications';
import { NotificationMessageType } from '../utils/configuration';

class NotificationService {
  constructor() {
    this.isInitialized = false;
    this.notifications = [];
    this.listeners = {};
    this.maxNotifications = 100; // Maximum number of notifications to store
    this.tokenAcquired = false;
    this.tokenRegistered = false;
  }

  // Initialize notification service
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Initialize SignalR
      await signalRService.initConnection();

      // Initialize Firebase for FCM
      await firebaseService.initialize();

      // Set up Firebase notification handler
      firebaseService.addNotificationCallback(this.handleFcmNotification.bind(this));

      this.isInitialized = true;
      
      // Try to get FCM token right away when initializing
      await this.acquireToken();
      
      console.log('Notification service initialized');
    } catch (error) {
      console.error('Error initializing notification service:', error);
    }
  }

  // Acquire FCM token (can be called on page load)
  async acquireToken() {
    if (this.tokenAcquired) {
      console.log('FCM token already acquired');
      return;
    }
    
    try {
      // Request permission and get FCM token
      const token = await firebaseService.requestPermissionAndGetToken();
      
      if (token) {
        this.tokenAcquired = true;
        console.log('Successfully acquired FCM token');
      }
    } catch (error) {
      console.error('Error acquiring FCM token:', error);
    }
  }

  // Register FCM token with backend (should be called after login)
  async registerTokenWithBackend() {
    if (this.tokenRegistered) {
      console.log('FCM token already registered with backend');
      return;
    }
    
    try {
      if (!firebaseService.token) {
        // Try to get token if we don't have one yet
        await this.acquireToken();
        
        if (!firebaseService.token) {
          console.warn('No FCM token available to register with backend');
          return;
        }
      }
      
      // Register token with backend
      const success = await firebaseService.registerTokenWithBackend();
      
      if (success) {
        this.tokenRegistered = true;
        console.log('Successfully registered FCM token with backend');
      }
    } catch (error) {
      console.error('Error registering FCM token with backend:', error);
    }
  }

  // Register for notifications (for backward compatibility)
  async registerForNotifications() {
    await this.registerTokenWithBackend();
  }

  // Unregister from notifications (e.g., on logout)
  async unregisterFromNotifications() {
    try {
      if (this.tokenRegistered) {
        await firebaseService.unregisterTokenWithBackend();
        this.tokenRegistered = false;
      }
      
      await signalRService.stopConnection();
    } catch (error) {
      console.error('Error unregistering from notifications:', error);
    }
  }

  // Add a listener for a specific notification type
  addNotificationListener(type, callback) {
    // Register with SignalR
    signalRService.addListener(type, (data) => {
      this.handleSignalRNotification(type, data);
      
      // Call the specific callback for this type
      if (typeof callback === 'function') {
        callback(data);
      }
    });

    // Store local listener for FCM notifications
    if (!this.listeners[type]) {
      this.listeners[type] = [];
    }
    
    // Add callback to our listeners if not already added
    const isCallbackRegistered = this.listeners[type].some(
      existingCallback => existingCallback.toString() === callback.toString()
    );
    
    if (!isCallbackRegistered) {
      this.listeners[type].push(callback);
    }
  }

  // Remove a listener for a specific notification type
  removeNotificationListener(type, callback) {
    // Remove from SignalR
    signalRService.removeListener(type, callback);
    
    // Remove from local listeners
    if (this.listeners[type]) {
      this.listeners[type] = this.listeners[type].filter(cb => cb !== callback);
      
      // Clean up if no more listeners for this type
      if (this.listeners[type].length === 0) {
        delete this.listeners[type];
      }
    }
  }

  // Handle notifications from SignalR
  handleSignalRNotification(type, data) {
    console.log(`Received SignalR notification of type ${type}:`, data);
    
    // Process notification
    const notification = {
      id: Date.now(),
      type,
      title: data.title || 'Thông báo mới',
      message: data.message || '',
      data: data.data || {},
      timestamp: new Date(),
      read: false,
      source: 'signalr'
    };
    
    // Add to notifications list
    this.addNotification(notification);
    
    // Show notification UI
    this.showNotificationUI(notification);
  }

  // Handle notifications from FCM (foreground)
  handleFcmNotification(payload) {
    console.log('Received FCM notification:', payload);
    
    // Extract data from FCM payload
    const notification = {
      id: Date.now(),
      type: payload.data?.type || 'default',
      title: payload.notification?.title || payload.data?.title || 'Thông báo mới',
      message: payload.notification?.body || payload.data?.message || '',
      data: payload.data || {},
      timestamp: new Date(),
      read: false,
      source: 'fcm'
    };
    
    // Add to notifications list
    this.addNotification(notification);
    
    // Show notification UI
    this.showNotificationUI(notification);
    
    // Call specific type listeners
    this.notifyTypeListeners(notification.type, notification.data);
  }

  // Add notification to the list
  addNotification(notification) {
    // Add to beginning of array
    this.notifications.unshift(notification);
    
    // Trim if exceeds max size
    if (this.notifications.length > this.maxNotifications) {
      this.notifications = this.notifications.slice(0, this.maxNotifications);
    }
  }

  // Show notification in UI
  showNotificationUI(notification) {
    // Show using react-notifications component
    ShowNotification(
      notification.message, 
      NotificationMessageType.Success
    );
  }

  // Notify type-specific listeners
  notifyTypeListeners(type, data) {
    if (this.listeners[type]) {
      this.listeners[type].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in notification listener for ${type}:`, error);
        }
      });
    }
  }

  // Get all stored notifications
  getNotifications() {
    return [...this.notifications];
  }

  // Mark notification as read
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
    }
  }

  // Mark all notifications as read
  markAllAsRead() {
    this.notifications.forEach(notification => {
      notification.read = true;
    });
  }
}

// Create a singleton instance
const notificationService = new NotificationService();
export default notificationService;
